import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store';

interface SuperAdminRouteProps {
  children: React.ReactNode;
}

export function SuperAdminRoute({ children }: SuperAdminRouteProps) {
  const location = useLocation();
  const { isAuthenticated, isLoading, user, fetchCurrentUser } = useAuthStore();
  const [isFetchingUser, setIsFetchingUser] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !user && !isLoading && !isFetchingUser) {
      setIsFetchingUser(true);
      fetchCurrentUser()
        .catch(() => {
          useAuthStore.getState().logout();
        })
        .finally(() => {
          setIsFetchingUser(false);
        });
    }
  }, [isAuthenticated, user, isLoading, isFetchingUser, fetchCurrentUser]);

  if (isLoading || isFetchingUser || (isAuthenticated && !user)) {
    return (
      <div className="flex justify-center items-center h-screen bg-white dark:bg-neolab-deep-green">
        <Loader2 className="w-12 h-12 text-neolab-green animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const isSuperAdmin = user?.role === 'super_admin';

  if (!isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center bg-white dark:bg-neolab-deep-green">
        <AlertCircle className="w-24 h-24 text-red-500 mb-6" />
        <h1 className="text-6xl font-black text-slate-900 dark:text-white mb-4">403</h1>
        <p className="text-xl text-slate-600 dark:text-slate-400 mb-8 max-w-md">
          Sorry, you are not authorized to access this page. Only super administrators can access
          this page.
        </p>
        <button
          type="button"
          onClick={() => window.history.back()}
          className="btn-primary flex items-center gap-2"
        >
          <ArrowLeft className="w-5 h-5" />
          Go Back
        </button>
      </div>
    );
  }

  return <>{children}</>;
}

export default SuperAdminRoute;
